import { Button, Checkbox, Form, Grid, Popup, Text } from '@approvalmax/ui/src/components';
import { domHelpers } from '@approvalmax/utils';
import { memo } from 'react';

import {
    AddressSection,
    BatchPaymentSection,
    ContactInformationSection,
    CurrencySection,
    SettingsSection,
    TaxSection,
} from './components';
import { useInitialData, useXeroCreateContactForm } from './XeroCreateContactPopup.hooks';
import { messages } from './XeroCreateContactPopup.messages';
import { XeroCreateContactPopupProps } from './XeroCreateContactPopup.types';

const qa = 'xero-contact-creation';

export const XeroCreateContactPopup = memo<XeroCreateContactPopupProps>((props) => {
    const { title } = props;

    const { xeroContext, isUKCompany, hideBatchPaymentInContact, initialValues, loading } = useInitialData();
    const { form, handleSubmit } = useXeroCreateContactForm(isUKCompany, xeroContext.trackingCategories, initialValues);

    const sameAsBillingAddress = form.watch('sameAsBillingAddress');

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Popup.Header
                title={title}
                qa={qa}
                progress={loading}
                actions={
                    <Button
                        type='submit'
                        data-qa={domHelpers.generateDataQa(qa, 'done-button')}
                        title={messages.doneButtonTitle}
                        disabled={loading}
                        color='blue80'
                        size='medium'
                    >
                        {messages.doneButtonText}
                    </Button>
                }
            />

            <Popup.Body qa={qa}>
                <Grid gap={32}>
                    <div>
                        <Text font='headline' fontSize='xxsmall' fontWeight='medium' spacing='16 0'>
                            {messages.contactInformationHeader}
                        </Text>

                        <ContactInformationSection qa={qa} />
                    </div>

                    <div>
                        <Text font='headline' fontSize='xxsmall' fontWeight='medium' spacing='16 0'>
                            {messages.billingAddressHeader}
                        </Text>

                        <AddressSection name='billingAddress' qa={domHelpers.generateDataQa(qa, 'billing-address')} />
                    </div>

                    <div>
                        <Grid gridTemplateColumns='max-content auto' alignItems='center' gap={16}>
                            <Text font='headline' fontSize='xxsmall' fontWeight='medium' spacing='16 0'>
                                {messages.deliveryAddressHeader}
                            </Text>

                            <Checkbox
                                name='sameAsBillingAddress'
                                data-qa={domHelpers.generateDataQa(qa, 'same-as-billing-address-checkbox')}
                            >
                                {messages.sameAsBillingCheckboxLabel}
                            </Checkbox>
                        </Grid>

                        {!sameAsBillingAddress && (
                            <AddressSection
                                name='deliveryAddress'
                                qa={domHelpers.generateDataQa(qa, 'delivery-address')}
                            />
                        )}
                    </div>

                    <Grid gridAutoFlow='column' gap={16}>
                        <div>
                            <Text font='headline' fontSize='xxsmall' fontWeight='medium' spacing='16 0'>
                                {messages.salesSettingsHeader}
                            </Text>

                            <SettingsSection
                                name='sales'
                                qa={domHelpers.generateDataQa(qa, 'sales')}
                                accounts={xeroContext.accounts}
                                trackingCategories={xeroContext.trackingCategories}
                            />
                        </div>

                        <div>
                            <Text font='headline' fontSize='xxsmall' fontWeight='medium' spacing='16 0'>
                                {messages.purchaseSettingsHeader}
                            </Text>

                            <SettingsSection
                                name='purchases'
                                qa={domHelpers.generateDataQa(qa, 'purchase')}
                                accounts={xeroContext.accounts}
                                trackingCategories={xeroContext.trackingCategories}
                            />
                        </div>
                    </Grid>

                    <div>
                        <Text font='headline' fontSize='xxsmall' fontWeight='medium' spacing='16 0'>
                            {messages.taxHeader}
                        </Text>

                        <TaxSection qa={qa} taxCodes={xeroContext.taxCodes} />
                    </div>

                    <div>
                        <Text font='headline' fontSize='xxsmall' fontWeight='medium' spacing='16 0'>
                            {messages.currencyHeader}
                        </Text>

                        <CurrencySection qa={qa} currencies={xeroContext.currencies} />
                    </div>

                    {!hideBatchPaymentInContact && (
                        <div>
                            <Text font='headline' fontSize='xxsmall' fontWeight='medium' spacing='16 0'>
                                {messages.batchPaymentHeader}
                            </Text>

                            <BatchPaymentSection qa={qa} isUKCompany={isUKCompany} />
                        </div>
                    )}
                </Grid>
            </Popup.Body>
        </Form>
    );
});

XeroCreateContactPopup.displayName = 'XeroCreateContactPopup';
